import { AlertCircle, CheckCircle2, ChevronRight, Clock, Plus } from "lucide-react";
import { Badge, Button, Card } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import type { PortalProject, PortalTicket } from "../../../lib/portal";
import { fmtDate, fmtDateTime, fmtAmount, genericStatusVariant } from "../../../lib/format";
import { Figure, RowButton } from "../../ui";
import type { InvoiceTotals } from "../invoices/lib";
import { ProgressBar } from "../projects/ProgressBar";

/**
 * What is owed, as one figure, with the late part held apart from the rest.
 *
 * The total is the server's (`summary.stats`), printed as it came. The split
 * under it is the same invoices the list beside this card shows, added up, and
 * it is drawn only when that list has loaded - a bar with one end missing would
 * be a picture of a number nobody computed.
 */
export function BalanceCard({
  outstandingCents,
  totals,
  actionLabel,
  onAction,
}: {
  outstandingCents: number;
  totals: InvoiceTotals | null;
  actionLabel: string;
  onAction: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const currency = totals?.currency ?? "EUR";
  const money = (cents: number) => fmtAmount(cents, currency, locale);
  const lateCents = totals?.overdueCents ?? 0;
  const lateShare = outstandingCents > 0 ? Math.min(100, Math.round((lateCents / outstandingCents) * 100)) : 0;

  return (
    <Card className="p-6">
      <Figure label={t("portal.overview.outstandingTotal")} value={money(outstandingCents)} size="lg" />

      {outstandingCents === 0 ? (
        <p className="mt-3 flex items-center gap-2 text-sm">
          <CheckCircle2 className="size-4 shrink-0 text-success" />
          {t("portal.statement.closingSettled")}
        </p>
      ) : (
        totals && (
          <>
            {/* The two parts differ in lightness as well as in hue, and each has
                its own glyph and words below, so the bar is never the only place
                the split is stated. */}
            <div aria-hidden="true" className="mt-5 flex h-2 gap-0.5">
              {lateShare > 0 && <div className="rounded-full bg-destructive" style={{ width: `${lateShare}%` }} />}
              {lateShare < 100 && <div className="flex-1 rounded-full bg-primary/25" />}
            </div>
            <dl className="mt-4 space-y-2 text-sm">
              {lateCents > 0 && (
                <div className="flex items-center justify-between gap-3">
                  <dt className="flex items-center gap-2">
                    <AlertCircle className="size-4 shrink-0 text-destructive" />
                    {t("portal.overview.overdue")}
                  </dt>
                  <dd className="font-semibold tabular-nums">{money(lateCents)}</dd>
                </div>
              )}
              {outstandingCents - lateCents > 0 && (
                <div className="flex items-center justify-between gap-3">
                  <dt className="flex items-center gap-2">
                    <Clock className="size-4 shrink-0 text-primary" />
                    {t("portal.overview.withinTerm")}
                  </dt>
                  <dd className="font-semibold tabular-nums">{money(outstandingCents - lateCents)}</dd>
                </div>
              )}
            </dl>
          </>
        )
      )}

      <Button variant="outline" className="mt-5 w-full" onClick={onAction}>
        {actionLabel}
      </Button>
    </Card>
  );
}

/**
 * The same total as one row, for a phone.
 *
 * The card above sits in the side rail, and on a phone the rail folds UNDER the
 * list. The amount owed is the first thing most readers look for, so here it is
 * a single wide row above the list instead, leading to the invoices.
 */
export function BalanceRow({
  outstandingCents,
  currency,
  onOpen,
}: {
  outstandingCents: number;
  currency: string;
  onOpen: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  return (
    <Card className="overflow-hidden lg:hidden">
      <RowButton onClick={onOpen} className="min-h-14 items-center justify-between gap-3 py-3">
        <span className="text-foreground/70">{t("portal.overview.outstandingTotal")}</span>
        <span className="flex items-center gap-1.5">
          <span className="font-display text-xl font-extrabold tabular-nums">
            {fmtAmount(outstandingCents, currency, locale)}
          </span>
          <ChevronRight className="text-muted-foreground" />
        </span>
      </RowButton>
    </Card>
  );
}

/** The projects that are still moving, with how far each has come. */
export function ActiveProjects({
  projects,
  onOpen,
  onAll,
}: {
  projects: PortalProject[];
  onOpen: (id: string) => void;
  onAll: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  return (
    <section aria-labelledby="overview-projects">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 id="overview-projects" className="text-lg font-bold tracking-tight">
          {t("portal.overview.projectsTitle")}
        </h2>
        {/* A text link is 20px tall on its own. Under `lg` it is a touch
            target, so it gets the height of one. */}
        <Button variant="link" className="h-auto p-0 max-lg:min-h-11" onClick={onAll}>
          {t("portal.overview.allProjects")}
        </Button>
      </div>
      <Card className="overflow-hidden">
        <ul className="divide-y divide-border">
          {projects.map((p) => (
            <li key={p.id}>
              <RowButton onClick={() => onOpen(p.id)} className="flex-col items-stretch gap-3">
                <span className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[15px] font-semibold">{p.name}</span>
                  <Badge variant={genericStatusVariant(p.status)}>{t(`portal.projectStatus.${p.status}`)}</Badge>
                </span>
                <span className="flex items-center gap-3">
                  <ProgressBar pct={p.progressPct} />
                  <span className="w-10 shrink-0 text-right text-xs font-semibold tabular-nums">{p.progressPct}%</span>
                </span>
                <span className="text-sm text-foreground/70">
                  {[
                    p.deadline ? `${t("portal.projects.deadline")} ${fmtDate(p.deadline, locale)}` : null,
                    t("portal.projects.tasks", { n: p._count.tasks }),
                    t("portal.projects.milestones", { n: p._count.milestones }),
                  ].filter(Boolean).join(" · ")}
                </span>
              </RowButton>
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}

/** The latest conversations with the desk, and the way to start a new one. */
export function SupportCard({
  tickets,
  onOpen,
  onAll,
  onNew,
}: {
  tickets: PortalTicket[];
  onOpen: (id: string) => void;
  onAll: () => void;
  onNew: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  return (
    <Card className="overflow-hidden">
      <div className="flex min-h-11 items-center justify-between gap-3 px-6 pt-4">
        <h2 className="font-bold tracking-tight">{t("portal.support.title")}</h2>
        {tickets.length > 0 && (
          <Button variant="link" className="h-auto p-0 max-lg:min-h-11" onClick={onAll}>
            {t("portal.overview.allTickets")}
          </Button>
        )}
      </div>
      {tickets.length === 0 ? (
        <p className="px-6 pt-2 text-sm text-foreground/70">{t("portal.overview.supportEmpty")}</p>
      ) : (
        <ul className="mt-2 divide-y divide-border border-y border-border">
          {tickets.map((tk) => (
            <li key={tk.id}>
              <RowButton onClick={() => onOpen(tk.id)} className="flex-col items-stretch gap-1.5 px-6 py-3">
                <span className="flex items-start justify-between gap-3">
                  <span className="min-w-0 font-semibold">{tk.subject}</span>
                  <Badge variant={genericStatusVariant(tk.status)} className="shrink-0">
                    {t(`portal.ticketStatus.${tk.status}`)}
                  </Badge>
                </span>
                <span className="text-xs text-foreground/70">
                  {tk.number} · {fmtDateTime(tk.updatedAt, locale)}
                </span>
              </RowButton>
            </li>
          ))}
        </ul>
      )}
      <div className="p-6 pt-4">
        <Button variant="secondary" className="w-full" onClick={onNew}>
          <Plus />
          {t("portal.support.new")}
        </Button>
      </div>
    </Card>
  );
}
